import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import { showSnackbar } from "@/store/reducers/app";
import Valkyrie, { viCheck, viSpinnerThird } from "@sippy-platform/valkyrie";

import { ListItem, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import PropTypes from "prop-types";

function MemberItem({ user, group, dispatch }) {
  const dispatcher = useDispatch();

  const [pending, setPending] = useState(false);

  const isActive = useMemo(
    () => !!group.members.find((_user) => _user.sippeer.id === user.id),
    [group.members, user.id],
  );

  const disableItem = useMemo(() => !isActive && group.members.length >= 50, [group.members.length, isActive]);

  function toggleMember() {
    setPending(true);

    if (isActive) {
      let index = group.members.findIndex((_user) => _user.sippeer.id === user.id);

      Promise.resolve(Api.deleteGroupMember(group, group.members[index]))
        .then(() => dispatch({ type: "removeMember", member: group.members[index] }))
        .finally(() => setPending(false));
    } else {
      // If not in the list yet, add behind
      if (group.members.length >= 50) {
        dispatcher(showSnackbar("Group size limit reached"));
        return;
      }

      // Find the sippeer, make a new member object and add it to the group
      const newMember = {
        sippeer: user,
        position: group.members.length + 1,
        ringSeconds: 10,
      };

      Promise.resolve(Api.postGroupMember(group, newMember))
        .then((response) =>
          dispatch({
            type: "addMember",
            member: {
              ...newMember,
              id: response.data.groupMember.id,
            },
          }),
        )
        .finally(() => setPending(false));
    }
  }

  return (
    <ListItem disablePadding key={user.id} sx={{ minHeight: 44 }}>
      <ListItemButton onClick={toggleMember} disabled={pending || disableItem} sx={{ minHeight: 44 }}>
        <ListItemAvatar>
          <Avatar text={user.name} />
        </ListItemAvatar>
        <ListItemText primary={user.name} secondary={user.exten} />
        {(pending || isActive) && (
          <ListItemIcon sx={{ minWidth: 16 }}>
            <Valkyrie icon={pending ? viSpinnerThird : viCheck} spin={pending} />
          </ListItemIcon>
        )}
      </ListItemButton>
    </ListItem>
  );
}

MemberItem.propTypes = {
  user: PropTypes.object,
  group: PropTypes.object,
  dispatch: PropTypes.func,
};

export default MemberItem;
